// src/hooks/useBrands.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchBrandsPaginated,
  fetchBrandStats,
  createBrand,
  updateBrand,
  deleteBrand,
  removeBrandImage,
  Brand,
} from '../admin/pages/brand/brandService';

// ─────────────────────────────────────────────────────────────────────────────
// QUERY KEYS
// ─────────────────────────────────────────────────────────────────────────────
export const BRAND_KEYS = {
  all: ['brands'] as const,
  lists: () => ['brands', 'list'] as const,
  paginated: (page: number, search: string, status: string) =>
    ['brands', 'list', page, search, status] as const,
  stats: () => ['brands', 'stats'] as const,
};

// ─────────────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────────────
export interface BrandPaginationParams {
  page: number;
  search: string;
  status: string;
  pageSize: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// useBrandsPaginated
// ─────────────────────────────────────────────────────────────────────────────
export const useBrandsPaginated = (params: BrandPaginationParams) => {
  return useQuery({
    queryKey: BRAND_KEYS.paginated(params.page, params.search, params.status),
    queryFn: () =>
      fetchBrandsPaginated(
        params.pageSize,
        params.page,
        params.search || undefined,
        params.status !== 'all' ? params.status : undefined
      ),
    staleTime: 1000 * 60 * 3,
    placeholderData: (prev: any) => prev,
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// useBrandStats
// ─────────────────────────────────────────────────────────────────────────────
export const useBrandStats = () => {
  return useQuery({
    queryKey: BRAND_KEYS.stats(),
    queryFn: fetchBrandStats,
    staleTime: 1000 * 60 * 5,
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// useBrandMutations
// ─────────────────────────────────────────────────────────────────────────────
export const useBrandMutations = () => {
  const queryClient = useQueryClient();

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: BRAND_KEYS.all });
  };

  // ── Create ────────────────────────────────────────────────────────────────
  const create = useMutation({
    mutationFn: ({
      brandData,
      imageFile,
      userId,
    }: {
      brandData: Omit<Brand, 'id' | 'createdAt' | 'updatedAt' | 'imageUrl' | 'slug'>;
      imageFile: File;
      userId: string;
    }) => createBrand(brandData, imageFile, userId),
    onSuccess: invalidateAll,
  });

  // ── Update ────────────────────────────────────────────────────────────────
  const update = useMutation({
    mutationFn: ({
      brandId,
      brandData,
      imageFile,
    }: {
      brandId: string;
      brandData: Partial<Omit<Brand, 'id' | 'createdAt' | 'updatedAt' | 'createdBy'>>;
      imageFile?: File;
    }) => updateBrand(brandId, brandData, imageFile),
    onSuccess: invalidateAll,
  });

  // ── Remove Image ──────────────────────────────────────────────────────────
  // Deletes the image from Firebase Storage and clears imageUrl in Firestore.
  const removeImage = useMutation({
    mutationFn: ({ brandId, imageUrl }: { brandId: string; imageUrl: string }) =>
      removeBrandImage(brandId, imageUrl),
    onSuccess: invalidateAll,
  });

  // ── Delete ────────────────────────────────────────────────────────────────
  const remove = useMutation({
    mutationFn: (brandId: string) => deleteBrand(brandId),
    onSuccess: invalidateAll,
  });

  return { create, update, removeImage, remove };
};